import { supabase } from '../lib/supabase';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getUserId = async () => {
  const { data: { user } } = await supabase?.auth?.getUser();
  return user?.id || null;
};

const getUserInfo = async () => {
  const { data: { user } } = await supabase?.auth?.getUser();
  return { id: user?.id || null, email: user?.email || null };
};

// ─── Audit Log ────────────────────────────────────────────────────────────────

export const auditLog = async (action, entityType, entityId, oldValues, newValues, screen) => {
  try {
    const { id: userId, email: userEmail } = await getUserInfo();
    if (!userId) return;
    await supabase?.from('audit_log')?.insert({
      user_id: userId,
      user_email: userEmail,
      action,
      entity_type: entityType,
      entity_id: entityId ? String(entityId) : null,
      old_values: oldValues || null,
      new_values: newValues || null,
      screen: screen || null,
    });
  } catch (_) {
    // audit failures are silent — never break the main operation
  }
};

export const auditLogService = {
  async getAll({ page = 0, pageSize = 50, action, entityType, dateFrom, dateTo } = {}) {
    const userId = await getUserId();
    if (!userId) return { data: [], error: null, count: 0 };
    let query = supabase?.from('audit_log')?.select('*', { count: 'exact' })?.eq('user_id', userId)?.order('created_at', { ascending: false });
    if (action) query = query?.eq('action', action);
    if (entityType) query = query?.eq('entity_type', entityType);
    if (dateFrom) query = query?.gte('created_at', dateFrom);
    if (dateTo) query = query?.lte('created_at', dateTo + 'T23:59:59Z');
    query = query?.range(page * pageSize, (page + 1) * pageSize - 1);
    const { data, error, count } = await query;
    return { data: data || [], error, count: count || 0 };
  },
};

// ─── Metals ───────────────────────────────────────────────────────────────────

export const metalsService = {
  async getAll() {
    const userId = await getUserId();
    if (!userId) return { data: [], error: null };
    const { data, error } = await supabase?.from('metals')?.select('*')?.eq('user_id', userId)?.order('name', { ascending: true });
    return { data: data || [], error };
  },

  async upsert(metal) {
    const userId = await getUserId();
    if (!userId) return { data: null, error: { message: 'Not authenticated' } };
    const { data, error } = await supabase?.from('metals')?.upsert({ ...metal, user_id: userId }, { onConflict: 'id' })?.select()?.single();
    if (!error) auditLog('UPDATE', 'metals', data?.id, null, data, 'settings');
    return { data, error };
  },

  async create(metal) {
    const userId = await getUserId();
    if (!userId) return { data: null, error: { message: 'Not authenticated' } };
    const { data, error } = await supabase?.from('metals')?.insert({ ...metal, user_id: userId, is_default: false })?.select()?.single();
    if (!error) auditLog('CREATE', 'metals', data?.id, null, data, 'settings');
    return { data, error };
  },

  async update(id, updates) {
    const userId = await getUserId();
    if (!userId) return { data: null, error: { message: 'Not authenticated' } };
    const { data: old } = await supabase?.from('metals')?.select('*')?.eq('id', id)?.single();
    const { data, error } = await supabase?.from('metals')?.update(updates)?.eq('id', id)?.eq('user_id', userId)?.select()?.single();
    if (!error) auditLog('UPDATE', 'metals', id, old, data, 'settings');
    return { data, error };
  },

  async delete(id) {
    const userId = await getUserId();
    if (!userId) return { error: { message: 'Not authenticated' } };
    const { data: old } = await supabase?.from('metals')?.select('*')?.eq('id', id)?.single();
    const { error } = await supabase?.from('metals')?.delete()?.eq('id', id)?.eq('user_id', userId);
    if (!error) auditLog('DELETE', 'metals', id, old, null, 'settings');
    return { error };
  },
};

// ─── Scrap Categories ─────────────────────────────────────────────────────────

export const categoriesService = {
  async getAll() {
    const userId = await getUserId();
    if (!userId) return { data: [], error: null };
    const { data, error } = await supabase?.from('scrap_categories')?.select('*, metals(id, name, benchmark_type, benchmark_symbol)')?.eq('user_id', userId)?.order('display_name', { ascending: true });
    return { data: data || [], error };
  },

  async getByMetal(metalId) {
    const userId = await getUserId();
    if (!userId) return { data: [], error: null };
    const { data, error } = await supabase?.from('scrap_categories')?.select('*')?.eq('user_id', userId)?.eq('metal_id', metalId)?.order('display_name', { ascending: true });
    return { data: data || [], error };
  },

  async create(category) {
    const userId = await getUserId();
    if (!userId) return { data: null, error: { message: 'Not authenticated' } };
    const { data, error } = await supabase?.from('scrap_categories')?.insert({ ...category, user_id: userId })?.select()?.single();
    if (!error) auditLog('CREATE', 'scrap_categories', data?.id, null, data, 'settings');
    return { data, error };
  },

  async update(id, updates) {
    const userId = await getUserId();
    if (!userId) return { data: null, error: { message: 'Not authenticated' } };
    const { data: old } = await supabase?.from('scrap_categories')?.select('*')?.eq('id', id)?.single();
    const { data, error } = await supabase?.from('scrap_categories')?.update(updates)?.eq('id', id)?.eq('user_id', userId)?.select()?.single();
    if (!error) auditLog('UPDATE', 'scrap_categories', id, old, data, 'settings');
    return { data, error };
  },

  async delete(id) {
    const userId = await getUserId();
    if (!userId) return { error: { message: 'Not authenticated' } };
    const { data: old } = await supabase?.from('scrap_categories')?.select('*')?.eq('id', id)?.single();
    const { error } = await supabase?.from('scrap_categories')?.delete()?.eq('id', id)?.eq('user_id', userId);
    if (!error) auditLog('DELETE', 'scrap_categories', id, old, null, 'settings');
    return { error };
  },
};

// ─── Batches ──────────────────────────────────────────────────────────────────

export const batchesService = {
  async getAll() {
    const userId = await getUserId();
    if (!userId) return { data: [], error: null };
    const { data, error } = await supabase?.from('batches')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });
    return { data: data || [], error };
  },

  async create(batch) {
    const userId = await getUserId();
    if (!userId) return { data: null, error: { message: 'Not authenticated' } };
    const batchWithWeightRemaining = {
      ...batch,
      weight_remaining: batch?.weight_remaining != null ? batch?.weight_remaining : batch?.weight_t,
    };
    const { data, error } = await supabase?.from('batches')?.insert({ ...batchWithWeightRemaining, user_id: userId })?.select()?.single();
    if (!error) auditLog('CREATE', 'batches', data?.id, null, data, 'batch-calculator');
    return { data, error };
  },

  async update(id, updates) {
    const userId = await getUserId();
    if (!userId) return { data: null, error: { message: 'Not authenticated' } };
    const { data: old } = await supabase?.from('batches')?.select('*')?.eq('id', id)?.single();
    const { data, error } = await supabase?.from('batches')?.update(updates)?.eq('id', id)?.eq('user_id', userId)?.select()?.single();
    if (!error) auditLog('UPDATE', 'batches', id, old, data, 'active-batch-management');
    return { data, error };
  },

  async delete(id) {
    const userId = await getUserId();
    if (!userId) return { error: { message: 'Not authenticated' } };
    const { data: old } = await supabase?.from('batches')?.select('*')?.eq('id', id)?.single();
    const { error } = await supabase?.from('batches')?.delete()?.eq('id', id)?.eq('user_id', userId);
    if (!error) auditLog('DELETE', 'batches', id, old, null, 'active-batch-management');
    return { error };
  },

  subscribeToChanges(userId, callback, channelSuffix = 'default') {
    const channel = supabase?.channel(`batches_realtime_${channelSuffix}`)?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'batches',
        filter: `user_id=eq.${userId}`,
      }, callback)?.subscribe();
    return channel;
  },
};

// ─── Sales ────────────────────────────────────────────────────────────────────

export const salesService = {
  async create(sale) {
    const userId = await getUserId();
    if (!userId) return { data: null, error: { message: 'Not authenticated' } };
    const { data, error } = await supabase?.from('sales')?.insert({ ...sale, user_id: userId })?.select()?.single();
    if (!error) auditLog('CREATE', 'sales', data?.id, null, data, 'inventory-management');
    return { data, error };
  },

  async getAll() {
    const userId = await getUserId();
    if (!userId) return { data: [], error: null };
    const { data, error } = await supabase?.from('sales')?.select('*')?.eq('user_id', userId)?.order('sold_at', { ascending: false });
    return { data: data || [], error };
  },
};

// ─── Market Ticks ─────────────────────────────────────────────────────────────

export const marketTicksService = {
  async getLatest() {
    const userId = await getUserId();
    if (!userId) return { data: [], error: null };
    // Fetch a wide window ordered by symbol then created_at desc so we can
    // deduplicate in JS keeping only the most-recent tick per symbol.
    const { data, error } = await supabase
      ?.from('market_ticks')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('symbol', { ascending: true })
      ?.order('created_at', { ascending: false })
      ?.limit(500);
    if (error) return { data: [], error };
    // Deduplicate: keep latest per symbol (first occurrence after ordering by created_at desc)
    const seen = new Set();
    const latest = [];
    for (const tick of (data || [])) {
      if (!seen?.has(tick?.symbol)) {
        seen?.add(tick?.symbol);
        latest?.push(tick);
      }
    }
    return { data: latest, error: null };
  },

  async upsertTick(tick) {
    const userId = await getUserId();
    if (!userId) return { data: null, error: { message: 'Not authenticated' } };
    const { data, error } = await supabase?.from('market_ticks')?.insert({ ...tick, user_id: userId, ts: new Date()?.toISOString() })?.select()?.single();
    if (!error) auditLog('CREATE', 'market_ticks', data?.id, null, data, 'market-data');
    return { data, error };
  },

  subscribeToChanges(userId, callback, channelSuffix = 'default') {
    const channel = supabase?.channel(`market_ticks_realtime_${channelSuffix}`)?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'market_ticks',
        filter: `user_id=eq.${userId}`,
      }, callback)?.subscribe();
    return channel;
  },
};

// ─── Risk Thresholds ──────────────────────────────────────────────────────────

export const riskThresholdsService = {
  async get() {
    const userId = await getUserId();
    if (!userId) return { data: null, error: null };
    const { data, error } = await supabase?.from('risk_thresholds')?.select('*')?.eq('user_id', userId)?.maybeSingle();
    return { data, error };
  },

  async upsert(thresholds) {
    const userId = await getUserId();
    if (!userId) return { data: null, error: { message: 'Not authenticated' } };
    const { data: old } = await supabase?.from('risk_thresholds')?.select('*')?.eq('user_id', userId)?.maybeSingle();
    const { data, error } = await supabase?.from('risk_thresholds')?.upsert({ ...thresholds, user_id: userId }, { onConflict: 'user_id' })?.select()?.single();
    if (!error) auditLog('UPDATE', 'risk_thresholds', data?.id, old, data, 'settings');
    return { data, error };
  },
};

// ─── Volatility Settings ──────────────────────────────────────────────────────

export const volatilityService = {
  async getAll() {
    const userId = await getUserId();
    if (!userId) return { data: [], error: null };
    const { data, error } = await supabase?.from('volatility_settings')?.select('*, metals(id, name)')?.eq('user_id', userId);
    return { data: data || [], error };
  },

  async upsertForMetal(metalId, settings) {
    const userId = await getUserId();
    if (!userId) return { data: null, error: { message: 'Not authenticated' } };
    const { data, error } = await supabase?.from('volatility_settings')?.upsert({ ...settings, user_id: userId, metal_id: metalId }, { onConflict: 'user_id,metal_id' })?.select()?.single();
    if (!error) auditLog('UPDATE', 'volatility_settings', data?.id, null, data, 'settings');
    return { data, error };
  },

  async upsertMany(settingsArray) {
    const userId = await getUserId();
    if (!userId) return { data: null, error: { message: 'Not authenticated' } };
    const rows = settingsArray?.map(s => ({ ...s, user_id: userId }));
    const { data, error } = await supabase?.from('volatility_settings')?.upsert(rows, { onConflict: 'user_id,metal_id' })?.select();
    if (!error) auditLog('UPDATE', 'volatility_settings', 'bulk', null, { count: rows?.length }, 'settings');
    return { data, error };
  },

  async create(settings) {
    const userId = await getUserId();
    if (!userId) return { data: null, error: { message: 'Not authenticated' } };
    const { data, error } = await supabase?.from('volatility_settings')?.insert({ ...settings, user_id: userId })?.select('*, metals(id, name)')?.single();
    if (!error) auditLog('CREATE', 'volatility_settings', data?.id, null, data, 'settings');
    return { data, error };
  },

  async update(id, settings) {
    const userId = await getUserId();
    if (!userId) return { data: null, error: { message: 'Not authenticated' } };
    const { data: old } = await supabase?.from('volatility_settings')?.select('*')?.eq('id', id)?.single();
    const { data, error } = await supabase?.from('volatility_settings')?.update(settings)?.eq('id', id)?.eq('user_id', userId)?.select('*, metals(id, name)')?.single();
    if (!error) auditLog('UPDATE', 'volatility_settings', id, old, data, 'settings');
    return { data, error };
  },

  async delete(id) {
    const userId = await getUserId();
    if (!userId) return { error: { message: 'Not authenticated' } };
    const { data: old } = await supabase?.from('volatility_settings')?.select('*')?.eq('id', id)?.single();
    const { error } = await supabase?.from('volatility_settings')?.delete()?.eq('id', id)?.eq('user_id', userId);
    if (!error) auditLog('DELETE', 'volatility_settings', id, old, null, 'settings');
    return { error };
  },
};

// ─── Global Shock Settings ────────────────────────────────────────────────────

export const globalShockService = {
  async get() {
    const userId = await getUserId();
    if (!userId) return { data: null, error: null };
    const { data, error } = await supabase?.from('risk_thresholds')?.select('id, global_shock_settings')?.eq('user_id', userId)?.maybeSingle();
    return { data, error };
  },

  async upsert(globalShock) {
    const userId = await getUserId();
    if (!userId) return { data: null, error: { message: 'Not authenticated' } };
    const { data, error } = await supabase?.from('risk_thresholds')?.upsert({ user_id: userId, global_shock_settings: globalShock }, { onConflict: 'user_id' })?.select('id, global_shock_settings')?.single();
    if (!error) auditLog('UPDATE', 'risk_thresholds', data?.id, null, { global_shock_settings: globalShock }, 'settings');
    return { data, error };
  },
};
